/**
 * CTR Price New – centralised variant-price updater.
 *
 * Updates the price display AND dispatches a `ctr:price-update` CustomEvent
 * so other components (buy-button, buy-once, etc.) can react to variant changes
 * without duplicating listeners.
 *
 * Relies on Shopify PUB_SUB_EVENTS.variantChange (primary) plus fallback
 * listeners on purchase-option, selling-plan and hidden variant input.
 */
(function () {
  /* ── DOM refs ── */
  var display = document.querySelector('[data-ctr-price-new]');
  if (!display) return;

  var compareEl    = display.querySelector('[data-price-new-compare]');
  var currentEl    = display.querySelector('[data-price-new-current]');
  var badgeEl      = display.querySelector('[data-price-new-badge]');
  var perServingEl = display.querySelector('[data-price-new-per-serving]');
  var savingsType  = display.dataset.savingsType || 'percent';
  var savingsLabel = display.dataset.savingsLabel || 'off';

  var jsonEl = document.querySelector('[data-ctr-price-new-product]') ||
               document.querySelector('[data-product-json]');
  var productData = null;
  if (jsonEl) {
    try { productData = JSON.parse(jsonEl.textContent); } catch (e) { /* noop */ }
  }
  if (!productData) return;

  var currency = display.dataset.currency || productData.currency || 'USD';

  var variantMetafields = {};
  var metafieldsEl = document.querySelector('[data-variant-metafields-new]');
  if (metafieldsEl) {
    try { variantMetafields = JSON.parse(metafieldsEl.textContent); } catch (e) { /* noop */ }
  }

  /* ── Helpers ── */
  function fmt(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(cents / 100);
  }

  function fmtCompact(cents) {
    var val = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: val % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(val);
  }

  function getVariant(id) {
    return productData.variants.find(function (v) { return v.id == id; });
  }

  function isSub() {
    var el = document.querySelector('[data-purchase-option][value="subscription"]');
    return el && el.checked;
  }

  function getSellingPlanId() {
    var el = document.querySelector('[data-selling-plan-select]');
    return el ? el.value : null;
  }

  /* ── Price calculation ── */
  function calc(variant) {
    if (!variant) return null;

    var currentPrice = variant.price;
    var comparePrice = variant.compare_at_price;
    var subscription = isSub();

    if (subscription && variant.selling_plan_allocations && variant.selling_plan_allocations.length) {
      var planId = getSellingPlanId();
      var alloc = planId
        ? variant.selling_plan_allocations.find(function (a) { return a.selling_plan_id == planId; })
        : variant.selling_plan_allocations[0];
      if (alloc && alloc.price) currentPrice = alloc.price;
      comparePrice = comparePrice || variant.price;
    }

    var savings = comparePrice && comparePrice > currentPrice ? comparePrice - currentPrice : 0;
    var pct = comparePrice && comparePrice > 0 ? Math.round((savings / comparePrice) * 100) : 0;

    return {
      currentPrice: currentPrice,
      comparePrice: comparePrice,
      savings: savings,
      pct: pct,
      variant: variant,
      isSubscription: subscription
    };
  }

  /* ── Render ── */
  function render(prices) {
    if (!prices) return;

    if (currentEl)  currentEl.textContent = fmt(prices.currentPrice);

    if (compareEl) {
      if (prices.comparePrice && prices.comparePrice > prices.currentPrice) {
        compareEl.textContent = fmt(prices.comparePrice);
        compareEl.style.display = '';
      } else {
        compareEl.textContent = '';
        compareEl.style.display = 'none';
      }
    }

    if (badgeEl) {
      if (prices.savings > 0 && prices.comparePrice > 0) {
        var amount = savingsType === 'fixed' ? fmt(prices.savings) : prices.pct + '%';
        badgeEl.textContent = savingsLabel === 'save'
          ? 'Save ' + amount
          : amount + ' OFF';
        badgeEl.style.display = '';
      } else {
        badgeEl.textContent = '';
        badgeEl.style.display = 'none';
      }
    }

    if (perServingEl && prices.variant) {
      var meta = variantMetafields[prices.variant.id];
      var bottles = meta ? meta.number_of_bottles : 1;
      var servings = meta ? meta.number_of_servings : 30;
      var total = bottles * servings;
      var perServing = (prices.currentPrice / 100) / total;
      perServingEl.textContent = '$' + perServing.toFixed(2);
    }
  }

  /* ── Dispatch so other components can react ── */
  function broadcast(prices) {
    document.dispatchEvent(new CustomEvent('ctr:price-update', {
      detail: {
        currentPrice: prices.currentPrice,
        comparePrice: prices.comparePrice,
        savings: prices.savings,
        pct: prices.pct,
        variant: prices.variant,
        isSubscription: prices.isSubscription,
        formatted: {
          currentPrice: fmt(prices.currentPrice),
          comparePrice: prices.comparePrice ? fmt(prices.comparePrice) : ''
        }
      }
    }));
  }

  /* ── Main update ── */
  function update(variant) {
    var prices = calc(variant);
    if (!prices) return;
    render(prices);
    broadcast(prices);
  }

  function updateFromId(variantId) {
    var v = getVariant(variantId);
    if (v) update(v);
  }

  /* ── Get current variant ID from form ── */
  function getCurrentId() {
    var form = document.querySelector('[data-type="add-to-cart-form"]');
    var input = form ? form.querySelector('input[name="id"]') : null;
    return input ? input.value : null;
  }

  /* ──────────────────────────────────────────────
     LISTENERS
     ────────────────────────────────────────────── */

  /* 1. Shopify PubSub – primary (variant-selects → product-info → variantChange) */
  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var variant = event && event.data && event.data.variant;
      if (variant) update(variant);
    });
  }

  /* 2. Purchase option change (subscription ↔ one-time) */
  document.querySelectorAll('[data-purchase-option]').forEach(function (el) {
    el.addEventListener('change', function () {
      var id = getCurrentId();
      if (id) updateFromId(id);
    });
  });

  /* 3. Selling-plan dropdown change */
  var spEl = document.querySelector('[data-selling-plan-select]');
  if (spEl) {
    spEl.addEventListener('change', function () {
      var id = getCurrentId();
      if (id) updateFromId(id);
    });
  }

  /* 4. Fallback – hidden variant input mutation + change */
  var form = document.querySelector('[data-type="add-to-cart-form"]');
  var variantInput = form ? form.querySelector('input[name="id"]') : null;

  if (variantInput) {
    new MutationObserver(function () {
      if (variantInput.value) updateFromId(variantInput.value);
    }).observe(variantInput, { attributes: true, attributeFilter: ['value'] });

    variantInput.addEventListener('change', function () {
      if (this.value) updateFromId(this.value);
    });
  }

  /* ── Initial render ── */
  var initId = getCurrentId() || (display.dataset.initialVariant || '');
  if (initId) updateFromId(initId);
})();
